// =============================================================================
// VandieZ admin-web -- Application root
// =============================================================================
// All protected routes nest inside <AdminLayout/>, which provides the
// topbar + sidebar shell.  Each page renders bare content; the shared
// chrome lives once in AdminLayout instead of being copy-pasted into
// every page.
// =============================================================================

import type React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AdminLayout } from "@/components/AdminLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import { CancellationPolicyPage } from "@/pages/CancellationPolicyPage";
import { CommissionRulesPage } from "@/pages/CommissionRulesPage";
import { PricingPage } from "@/pages/PricingPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { DisputeDetailPage } from "@/pages/DisputeDetailPage";
import { DisputesPage } from "@/pages/DisputesPage";
import { KycQueuePage } from "@/pages/KycQueuePage";
import { KycReviewFlagsPage } from "@/pages/KycReviewFlagsPage";
import { KycUserDetailPage } from "@/pages/KycUserDetailPage";
import { LoadDetailPage } from "@/pages/LoadDetailPage";
import { LoadsListPage } from "@/pages/LoadsListPage";
import { LocationsPage } from "@/pages/LocationsPage";
import { RoutesPage } from "@/pages/RoutesPage";
import { BusinessMastersPage } from "@/pages/BusinessMastersPage";
import { ZonesPage } from "@/pages/ZonesPage";
import { AtRiskPage } from "@/pages/AtRiskPage";
import { BankVerificationQueuePage } from "@/pages/BankVerificationQueuePage";
import { AuditLogPage } from "@/pages/AuditLogPage";
import { FraudPage } from "@/pages/FraudPage";
import { InboxPage } from "@/pages/InboxPage";
import { SystemHealthPage } from "@/pages/SystemHealthPage";
import { LoginPage } from "@/pages/LoginPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { OpsPage } from "@/pages/OpsPage";
import { PayoutsPage } from "@/pages/PayoutsPage";
import PromotionDetailPage from "@/pages/PromotionDetailPage";
import { PromotionsIndexPage } from "@/pages/PromotionsIndexPage";
import PromotionsPendingPage from "@/pages/PromotionsPendingPage";
import { RoleDetailPage } from "@/pages/RoleDetailPage";
import { RolesListPage } from "@/pages/RolesListPage";
import { SmsLogDetailPage } from "@/pages/SmsLogDetailPage";
import { SmsLogPage } from "@/pages/SmsLogPage";
import { SmsSettingsPage } from "@/pages/SmsSettingsPage";
import { SupportTicketsPage } from "@/pages/SupportTicketsPage";
import { TicketDetailPage } from "@/pages/TicketDetailPage";
import { TicketsPage } from "@/pages/TicketsPage";
import { BidCallDetailPage } from "@/pages/BidCallDetailPage";
import { BidCallsPage } from "@/pages/BidCallsPage";
import { TripDetailPage } from "@/pages/TripDetailPage";
import { TripsLivePage } from "@/pages/TripsLivePage";
import { UserDetailPage } from "@/pages/UserDetailPage";
import { UsersListPage } from "@/pages/UsersListPage";
import { VehicleDetailPage } from "@/pages/VehicleDetailPage";
import { VehicleQueuePage } from "@/pages/VehicleQueuePage";
import { VehicleTransfersPage } from "@/pages/VehicleTransfersPage";
import { VehiclesSearchPage } from "@/pages/VehiclesSearchPage";
import { WalletAuditPage } from "@/pages/WalletAuditPage";

export function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected — shared topbar + sidebar shell */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/kyc/queue" element={<KycQueuePage />} />
            <Route
              path="/kyc/review-flags"
              element={<KycReviewFlagsPage />}
            />
            <Route path="/kyc/users/:userId" element={<KycUserDetailPage />} />
            <Route path="/vehicles/queue" element={<VehicleQueuePage />} />
            <Route path="/vehicles" element={<VehiclesSearchPage />} />
            <Route path="/vehicles/:vehicleId" element={<VehicleDetailPage />} />
            <Route
              path="/vehicle-transfers/pending"
              element={<VehicleTransfersPage />}
            />
            <Route path="/ops" element={<OpsPage />} />
            <Route path="/payouts" element={<PayoutsPage />} />
            <Route
              path="/bank-accounts/pending"
              element={<BankVerificationQueuePage />}
            />
            <Route path="/promotions" element={<PromotionsIndexPage />} />
            <Route path="/promotions/awards/pending" element={<PromotionsPendingPage />} />
            <Route path="/promotions/:code" element={<PromotionDetailPage />} />
            <Route path="/trips/live" element={<TripsLivePage />} />
            <Route path="/trips/:tripId" element={<TripDetailPage />} />
            <Route path="/bid-calls" element={<BidCallsPage />} />
            <Route path="/bid-calls/:callId" element={<BidCallDetailPage />} />
            <Route path="/loads" element={<LoadsListPage />} />
            <Route path="/loads/:loadId" element={<LoadDetailPage />} />
            <Route path="/commission/rules" element={<CommissionRulesPage />} />
            <Route
              path="/cancellation/policies"
              element={<CancellationPolicyPage />}
            />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/masterdata/locations" element={<LocationsPage />} />
            <Route path="/masterdata/zones" element={<ZonesPage />} />
            <Route path="/masterdata/routes" element={<RoutesPage />} />
            <Route
              path="/masterdata/business"
              element={<BusinessMastersPage />}
            />
            <Route path="/disputes" element={<DisputesPage />} />
            <Route path="/disputes/:disputeId" element={<DisputeDetailPage />} />
            <Route path="/mediation" element={<SupportTicketsPage />} />
            {/* Legacy URL — any bookmarked /support/tickets keeps resolving. */}
            <Route path="/support/tickets" element={<SupportTicketsPage />} />
            <Route path="/wallet/audit" element={<WalletAuditPage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
            <Route path="/settings/sms" element={<SmsSettingsPage />} />
            <Route path="/settings/sms/log" element={<SmsLogPage />} />
            <Route path="/settings/sms/log/:logId" element={<SmsLogDetailPage />} />
            <Route path="/users" element={<UsersListPage />} />
            <Route path="/users/:userId" element={<UserDetailPage />} />
            <Route path="/roles" element={<RolesListPage />} />
            <Route path="/roles/:roleId" element={<RoleDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/fraud" element={<FraudPage />} />
            <Route path="/at-risk" element={<AtRiskPage />} />
            <Route path="/system-health" element={<SystemHealthPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
